import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Collapse,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar
} from "@mui/material";
import { supabase } from "../lib/supabase";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PersonalInstallmentForm from "../components/PersonalInstallmentForm";
import EditPersonalInstallmentForm from "../components/EditPersonalInstallmentForm";
import AddIcon from "@mui/icons-material/Add";
import { formatDateWithoutTimezone } from "./CashFlowPersonal";

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

function groupBy(arr, key) {
  return arr.reduce((acc, item) => {
    const group = item[key];
    if (!group) return acc;
    if (!acc[group]) acc[group] = [];
    acc[group].push(item);
    return acc;
  }, {});
}

function PersonalInstallments() {
  const [installments, setInstallments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openRows, setOpenRows] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [installmentToDelete, setInstallmentToDelete] = useState(null);
  const [installmentToEdit, setInstallmentToEdit] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    const fetchInstallments = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("personal_transactions")
        .select(`
          *,
          category:category_id (name)
        `)
        .not("installment_group_id", "is", null)
        .is("deleted_at", null)
        .order("installment_group_id", { ascending: false })
        .order("installment_number", { ascending: true });

      if (error) {
        setInstallments([]);
        setLoading(false);
        return;
      }

      const grouped = groupBy(data, "installment_group_id");
      setInstallments(Object.values(grouped));
      setLoading(false);
    };

    fetchInstallments();
  }, []);

  const handleToggleRow = (groupId) => {
    setOpenRows((prev) => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const handleInstallmentAdded = () => {
    setOpenModal(false);
    fetchInstallments();
  };

  const fetchInstallments = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("personal_transactions")
      .select(`
        *,
        category:category_id (name)
      `)
      .not("installment_group_id", "is", null)
      .is("deleted_at", null)
      .order("installment_group_id", { ascending: false })
      .order("installment_number", { ascending: true });

    if (error) {
      setInstallments([]);
      setLoading(false);
      return;
    }

    const grouped = groupBy(data, "installment_group_id");
    setInstallments(Object.values(grouped));
    setLoading(false);
  };

  const handleDeleteClick = (installmentGroup) => {
    setInstallmentToDelete(installmentGroup);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!installmentToDelete) return;

    console.log('Tentando deletar parcelamento:', installmentToDelete[0].installment_group_id);
    
    try {
      const { data, error } = await supabase
        .from('personal_transactions')
        .update({ deleted_at: new Date().toISOString() })
        .eq('installment_group_id', installmentToDelete[0].installment_group_id)
        .select();

      console.log('Resultado da operação:', { data, error });

      if (error) {
        console.error('Erro do Supabase:', error);
        throw error;
      }

      console.log('Parcelamento deletado com sucesso');
      console.log('Disparando evento personalInstallmentDeleted...');
      // Small delay to ensure database has processed the deletion
      setTimeout(() => {
        window.dispatchEvent(new Event('personalInstallmentDeleted'));
        console.log('Evento personalInstallmentDeleted disparado');
      }, 100);
      setSnackbar({
        open: true,
        message: 'Parcelamento deletado com sucesso',
        severity: 'success'
      });
      setDeleteConfirmOpen(false);
      setInstallmentToDelete(null);
      fetchInstallments();
    } catch (error) {
      console.error('Erro ao deletar parcelamento:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao deletar parcelamento: ' + error.message,
        severity: 'error'
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setInstallmentToDelete(null);
  };

  const handleEditClick = (installmentGroup) => {
    setInstallmentToEdit(installmentGroup);
    setEditModalOpen(true);
  };

  const handleEditCancel = () => {
    setEditModalOpen(false);
    setInstallmentToEdit(null);
  };

  const handleInstallmentUpdated = () => {
    setEditModalOpen(false);
    setInstallmentToEdit(null);
    fetchInstallments();
    setSnackbar({
      open: true,
      message: 'Parcelamento atualizado com sucesso',
      severity: 'success'
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          Parcelamentos Pessoais
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenModal(true)}
        >
          Novo Parcelamento
        </Button>
      </Box>
      {loading ? (
        <CircularProgress />
      ) : installments.length === 0 ? (
        <Typography>Nenhum parcelamento pessoal encontrado.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Descrição</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell>Valor Total</TableCell>
                <TableCell>Parcelas</TableCell>
                <TableCell>Data Inicial</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {installments.map((parcelas, idx) => {
                const first = parcelas[0];
                const total = parcelas.reduce((sum, p) => sum + Math.abs(p.value), 0);
                return (
                  <React.Fragment key={first.installment_group_id}>
                    <TableRow>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleRow(first.installment_group_id)}
                        >
                          {openRows[first.installment_group_id] ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>{first.description}</TableCell>
                      <TableCell>{first.category?.name || 'Sem categoria'}</TableCell>
                      <TableCell>{formatCurrency(total)}</TableCell>
                      <TableCell>
                        {first.installment_count}x
                      </TableCell>
                      <TableCell>{formatDateWithoutTimezone(first.date)}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditClick(parcelas)}
                          disabled={first.deleted_at}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(parcelas)}
                          disabled={first.deleted_at}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                        <Collapse in={openRows[first.installment_group_id]} timeout="auto" unmountOnExit>
                          <Box margin={1}>
                            <Typography variant="subtitle1" gutterBottom>
                              Parcelas
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Parcela</TableCell>
                                  <TableCell>Data</TableCell>
                                  <TableCell>Valor</TableCell>
                                  <TableCell>Status</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {parcelas.map((p) => (
                                  <TableRow key={p.id}>
                                    <TableCell>
                                      {p.installment_number}/{p.installment_count}
                                    </TableCell>
                                    <TableCell>{formatDateWithoutTimezone(p.date)}</TableCell>
                                    <TableCell>{formatCurrency(Math.abs(p.value))}</TableCell>
                                    <TableCell>
                                      {p.deleted_at ? "Cancelada" : "Pendente"}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Novo Parcelamento Pessoal</DialogTitle>
        <DialogContent>
          <PersonalInstallmentForm onInstallmentAdded={handleInstallmentAdded} />
        </DialogContent>
      </Dialog>

      {/* Modal de confirmação para deletar */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja deletar este parcelamento? Esta ação não pode ser desfeita.
          </Typography>
          {installmentToDelete && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Detalhes:</Typography>
              <Typography>
                {installmentToDelete[0].description} - {installmentToDelete.length}x parcelas
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Deletar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para editar */}
      <Dialog
        open={editModalOpen}
        onClose={handleEditCancel}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Editar Parcelamento Pessoal</DialogTitle>
        <DialogContent>
          {installmentToEdit && (
            <EditPersonalInstallmentForm
              installmentGroup={installmentToEdit}
              onInstallmentUpdated={handleInstallmentUpdated}
              onCancel={handleEditCancel}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar para notificações */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PersonalInstallments;
